import type { UsageKind } from '@rp/contracts'

const dayLabel = (day: string): string => {
  const [, month, date] = day.split('-')
  return month === undefined || date === undefined ? day : `${Number(month)} 月 ${Number(date)} 日`
}

export const USAGE_COPY = {
  title: '用量',
  loading: '載入用量…',
  forbidden: '你沒有查看用量的權限',
  forbiddenBody: '只有管理員可以查看公司的用量與預算。',
  budgetHeading: '本月 AI 用量',
  period: (start: string, end: string) => `${start} 至 ${end}`,
  used: '已用',
  limit: '本月上限',
  remaining: '尚餘',
  adjustBudget: '調整',
  adjustBudgetLabel: '調整每月 token 預算',
  saveBudget: '儲存預算',
  savingBudget: '儲存中…',
  cancelBudget: '取消',
  monthlyTokenBudget: '每月 token 預算',
  monthlyTokenBudgetHint:
    '用完後本月的 AI 分類、建議與知識庫匯入會暫停，下個月 1 日自動重置。調低到比本月已用的還少，或設為 0 會立即暫停。',
  monthlyTokenBudgetGrouped: (grouped: string) => `= ${grouped} tokens`,
  monthlyTokenBudgetInvalid: (max: string) => `請輸入 0 到 ${max} 之間的整數，修正後才能儲存。`,
  costHeading: '費用估算（參考）',
  costNeedsPrices: '填入下面各家 AI 的單價後，才能估算費用。',
  costMonth: '本月預估費用',
  costBudget: '每月預算約值',
  costAbout: (amount: string) => `約 ${amount}`,
  usd: (amount: string) => `US$${amount}`,
  costUnderMinimum: (minimum: string) => `不到 ${minimum}`,
  costSettingsFailed: '讀不到單價設定，請重新整理頁面再試一次。',
  costBudgetUnknown: '本月還沒有已填單價的用量，暫時無法換算。',
  costUnpriced: (tokens: string) => `${tokens} tokens 沒有填單價，沒有算進去。`,
  costHow:
    '怎麼算：各家本月用量 × 該家單價 ÷ 1,000,000，加總就是本月預估費用；每月預算約值是預算的 token 數乘以本月已填單價用量的平均單價。因為輸入和輸出 token 沒有分開記錄，每家只能填一個平均單價。',
  costDisclaimer: '這只是依你填的單價粗估，不保證正確，實際金額以各家 AI 的帳單為準。',
  pricesHeading: '各家 AI 單價（美元／每 100 萬 tokens，全公司共用）',
  pricesReference: (price: number) => `參考 ${price}`,
  pricesReferenceNote: '參考值來自 2026-10-28 的 Vertex AI 價目表，Gemini 以輸入八成、輸出兩成估算；請以你們的合約價為準。',
  savePrices: '儲存單價',
  savingPrices: '儲存中…',
  pricesSaved: '單價已儲存',
  pricesConflict: '單價剛被其他人改過，已經換成最新的單價，請確認後再儲存。',
  pricesConflictReloading: '單價剛被其他人改過，正在讀取最新的單價…',
  pricesConflictReloadFailed: '單價剛被其他人改過，但讀不到最新的單價，請重新整理頁面再試。',
  pricesLoading: '讀取單價中…',
  reloadingPrices: '讀取最新單價中…',
  priceInvalid: (max: string) => `單價要是 0 到 ${max} 之間的數字`,
  usedRatio: (percent: number) => `已用 ${percent}%`,
  meterLabel: '本月 token 用量',
  paceMarkLabel: (percent: number) => `月份已過 ${percent}%`,
  elapsed: (elapsed: number, total: number) => `本月第 ${elapsed} 日，共 ${total} 日`,
  onTrackTitle: '照這個用法，本月預算夠用',
  onTrackBody: (projected: string) => `按目前速度，本月大約會用 ${projected} tokens，在上限之內。`,
  overspendingTitle: (day: string) => `照這個用法，${dayLabel(day)} 就會用完本月預算`,
  overspendingTitleUndated: '照這個用法，本月會超出預算',
  overspendingBody: (projected: string) =>
    `按目前速度，本月大約會用 ${projected} tokens。用完之後，AI 分類、建議與知識庫匯入會暫停：訊息照樣收得到，但要同事自己寫回覆。想提早調整，可以按「本月上限」旁的「調整」改每月 token 預算。`,
  exhausted: '本月 AI 用量已達上限，AI 分類、建議與知識庫匯入已暫停。可以按「本月上限」旁的「調整」提高每月 token 預算。',
  nearLimit: '本月 AI 用量接近上限，請留意。',
  volumeHeading: '本月處理量',
  kinds: {
    ingested: '已接收訊息',
    classified: '已分類',
    suggested: '已產生建議',
    replied: '已回覆',
    llm_tokens: 'AI tokens',
    kb_chunks: '知識庫片段',
  } satisfies Record<UsageKind, string>,
  providerHeading: '各 AI provider 用量',
  providerless: '內嵌模型',
  trendHeading: '最近 30 天',
  trendEmpty: '最近 30 天未有用量紀錄。',
  trendDayLabel: (day: string, tokens: string) => `${day}：${tokens} tokens`,
  updatedAt: (at: string) => `更新於 ${at}`,
  neverUpdated: '本月未有用量紀錄',
} as const
